import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { useForm, Controller } from 'react-hook-form';

import {
  Box,
  Container,
  Typography,
  Paper,
  Chip,
  LinearProgress,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Button,
  Tooltip,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Stack,
  Snackbar,
  Alert,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { MuiTelInput } from 'mui-tel-input';
import QRCode from 'qrcode';
import { useAuth } from '../auth';
import {
  getProfile, getMembershipSummary, getRewardPointsSummary, getReferralCode, updateProfile,
  addWalletCard, downloadFromUrl
} from '../api/profile';
import { getOrders } from '../api/orders';
import {
  ProfileResponse,
  MembershipStatus,
  RewardPoint,
  ReferralCode,
  WalletResponse,
} from '../types/profile';
import type { Order } from '../api/orders';
import { useCart } from '@/cart/CartManager';
import { useConfig } from '../api/config';


interface ProfileFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  birthMonth: string;
  birthDay: string;
  birthYear: string;
  gender: string;
}

export default function Profile() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated, isLoading: authLoading, isProfileLoaded, logout } = useAuth();
  const [profileData, setProfileData] = useState<ProfileResponse | null>(null);
  const [membershipData, setMembershipData] = useState<MembershipStatus | null>(null);
  const [rewardPointsData, setRewardPointsData] = useState<RewardPoint | null>(null);
  const [ordersData, setOrdersData] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [pointsDialogOpen, setPointsDialogOpen] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [referralCodeData, setReferralCodeData] = useState<ReferralCode | null>(null);
  const [updatingProfile, setUpdatingProfile] = useState(false);
  const [isAddingToWallet, setIsAddingToWallet] = useState(false);
  const [walletError, setWalletError] = useState<string | null>(null);
  const [isCertificateError, setIsCertificateError] = useState(false);
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'info'
  });
  const { clearCart } = useCart();
  const { config } = useConfig();

  // Form setup
  const {
    control,
    handleSubmit,
    setValue,
    register,
  } = useForm<ProfileFormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      birthMonth: '',
      birthDay: '',
      birthYear: '',
      gender: '',
    },
  });


  useEffect(() => {
    // AuthGuard handles authentication, no need to check here
    // Just proceed with data fetching when conditions are met

    // Only fetch data if authenticated and profile is loaded
    if (!isAuthenticated || !isProfileLoaded) {
      console.log('Waiting for authentication and profile to load...'); // Debug log
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('Fetching profile data...'); // Debug log

        const [profile, membership, rewardPoints, orders] = await Promise.all([
          getProfile(),
          getMembershipSummary(),
          getRewardPointsSummary(),
          getOrders()
        ]);

        console.log('Profile data received:', profile); // Debug log
        console.log('Membership data received:', membership); // Debug log
        console.log('Reward points data received:', rewardPoints); // Debug log
        console.log('Orders data received:', orders); // Debug log

        setProfileData(profile);
        setMembershipData(membership);
        setRewardPointsData(rewardPoints);
        setOrdersData(orders);

        // Fetch referral code separately
        try {
          const referralCodeResponse = await getReferralCode();
          // The API returns { referralCode: { code: "REFTPK69", ... } }
          setReferralCodeData(referralCodeResponse.referralCode);
        } catch (referralError) {
          console.error('Error fetching referral code:', referralError);
          // Don't fail the entire profile load if referral code fails
        }
      } catch (error) {
        console.error('Error fetching profile data:', error);
        setError('Failed to load profile data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isAuthenticated, isProfileLoaded]);

  const handleLogout = async () => {
    try {
      // Sign out from Firebase
      clearCart();
      logout();
      navigate('/sign-in');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleQrDialogOpen = async () => {
    try {
      if (membershipData?.currentMembership?.membershipNumber) {
        const qrData = `${membershipData.currentMembership.membershipNumber}`;
        const dataUrl = await QRCode.toDataURL(qrData, {
          width: 200,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        });
        setQrCodeDataUrl(dataUrl);
      }
      setQrDialogOpen(true);
    } catch (error) {
      console.error('Error generating QR code:', error);
      setQrDialogOpen(true);
    }
  };

  const handleQrDialogClose = () => {
    setQrDialogOpen(false);
  };

  const handlePointsDialogOpen = () => {
    setPointsDialogOpen(true);
  };

  const handlePointsDialogClose = () => {
    setPointsDialogOpen(false);
  };

  const handleProfileDialogOpen = () => {
    setProfileDialogOpen(true);
  };

  const handleProfileDialogClose = () => {
    setProfileDialogOpen(false);
  };

  const onSubmitProfile = async (data: ProfileFormData) => {
    console.log('Profile form data:', data);

    try {
      setUpdatingProfile(true);

      // Prepare the data for the API (exclude email as it can't be updated)
      const updateData = {
        firstName: data.firstName,
        lastName: data.lastName,
        phone: data.phone,
        birthMonth: data.birthMonth,
        birthDay: data.birthDay,
        birthYear: data.birthYear,
        gender: data.gender,
      };

      // Call the update profile API
      const updatedProfile = await updateProfile(updateData);

      // Update the local state with the new profile data
      setProfileData(updatedProfile);

      // Show success toast
      setToast({ open: true, message: 'Profile updated successfully!', severity: 'success' });

      // Close the dialog only on success
      setProfileDialogOpen(false);

    } catch (error) {
      console.error('Error updating profile:', error);
      // Show error toast but keep dialog open
      setToast({ open: true, message: 'Failed to update profile. Please try again.', severity: 'error' });
    } finally {
      setUpdatingProfile(false);
    }
  };

  // Set form values when profile data is loaded
  useEffect(() => {
    if (profileData?.customer) {
      const customer = profileData.customer;
      setValue('firstName', customer.firstName || '');
      setValue('lastName', customer.lastName || '');
      setValue('email', customer.email || '');
      setValue('phone', customer.phone || '');

      // Set birth date components
      if (customer.birthMonth) {
        setValue('birthMonth', customer.birthMonth.toString());
      }
      if (customer.birthDay) {
        setValue('birthDay', customer.birthDay.toString());
      }
      if (customer.birthYear) {
        setValue('birthYear', customer.birthYear.toString());
      }

      setValue('gender', customer.gender || '');
    }
  }, [profileData, setValue]);

  // Calculate order counts by status
  const getOrderCounts = () => {
    if (!ordersData || ordersData.length === 0) {
      return { awaitingShipment: 0, shipped: 0, readyForPickup: 0 };
    }

    const counts = {
      awaitingShipment: 0,
      shipped: 0,
      readyForPickup: 0
    };

    ordersData.forEach(order => {
      const status = order.order.status;
      if (status === 'pending' || status === 'processing' || status === 'paid') {
        counts.awaitingShipment++;
      } else if (status === 'shipped' || status === 'delivered') {
        counts.shipped++;
      } else if (status === 'ready_for_pickup') {
        counts.readyForPickup++;
      }
    });

    return counts;
  };

  async function handleAddCard() {
    try {
      setIsAddingToWallet(true);
      setWalletError(null);
      setIsCertificateError(false);

      const resp: WalletResponse = await addWalletCard();
      const { downloadUrl, serialNumber } = resp.pass;

      // Optional nicer filename based on serialNumber
      const filename = `membership-${serialNumber}.pkpass`;

      await downloadFromUrl(downloadUrl, filename);

      // Show toast notification
      setToast({
        open: true,
        message: resp.isUpdate
          ? 'Wallet pass updated successfully!'
          : 'Wallet pass added successfully!',
        severity: 'success'
      });

    } catch (error: unknown) {
      console.error('Error adding wallet card:', error);

      let errorMessage = t('profile.wallet.failedToAdd');
      let isCertificateError = false;

      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as { response?: { data?: { message?: string } } };
        if (apiError.response?.data?.message) {
          const serverMessage = apiError.response.data.message;

          // Check for specific certificate configuration errors
          if (serverMessage.includes('certificate configuration') ||
            serverMessage.includes('certificate') ||
            serverMessage.includes('Apple Wallet pass') ||
            serverMessage.includes('PassKit')) {
            isCertificateError = true;
            setIsCertificateError(true);
            errorMessage = t('profile.wallet.serviceUnavailable');
          } else {
            errorMessage = serverMessage;
          }
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      setWalletError(errorMessage);
      setToast({
        open: true,
        message: errorMessage,
        severity: 'error'
      });

      // If it's a certificate error, show additional help
      if (isCertificateError) {
        setTimeout(() => {
          setWalletError('Wallet pass service is temporarily unavailable. Please try again later or contact support if the issue persists.');
        }, 3000);
      }
    } finally {
      setIsAddingToWallet(false);
    }
  }

  // AuthGuard handles authentication check, so we only need to handle loading states

  if (authLoading || !isAuthenticated || !isProfileLoaded) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>
          {authLoading ? t('profile.loading.initializing') : !isAuthenticated ? t('profile.loading.authenticating') : t('profile.loading.profile')}
        </Typography>
      </div>
    );
  }

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', py: 4 }}>
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
            <CircularProgress size={60} sx={{ color: '#b8957b' }} />
          </Box>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', py: 4 }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
            <Typography
              onClick={() => window.location.reload()}
              sx={{
                color: '#b8957b',
                cursor: 'pointer',
                textDecoration: 'underline',
                '&:hover': { opacity: 0.8 }
              }}
            >
              {t('profile.error.retry')}
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  if (!profileData || !membershipData || !rewardPointsData) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', py: 4 }}>
        <Container maxWidth="md">
          <Typography>{t('profile.error.failedToLoad')}</Typography>
        </Container>
      </Box>
    );
  }

  const { customer } = profileData;
  const { currentMembership, nextTier, calculations } = membershipData;
  const { totalPoints } = rewardPointsData;

  // Calculate progress percentage for next tier
  const progressPercentage = nextTier && calculations && currentMembership?.membershipLevel
    ? ((calculations.currentSpent - currentMembership.membershipLevel.minSpendAmount) / (nextTier.target - currentMembership.membershipLevel.minSpendAmount)) * 100
    : 0;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Membership Card and Details - Side by Side on Desktop */}
      <Container maxWidth="md" sx={{ py: { xs: 2, md: 3 } }}>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2, md: 2 },
          mb: 3
        }}>
          {/* Membership Card - 50% width on desktop */}
          <Box sx={{
            flex: { xs: '1', md: '0 0 50%' },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginLeft: { xs: 0, md: '-20px' },
          }}>
            <Tooltip title={t('profile.membershipCard.qrTooltip')} placement="top">
              <Box
                component="img"
                src={membershipData?.membershipCardImageUrl || currentMembership?.membershipLevel?.cardImageUrl || '/images/account_member_card.png'}
                alt="Membership Card"
                onClick={handleQrDialogOpen}
                sx={{
                  width: '100%',
                  maxWidth: { xs: '100%', md: '400px' },
                  height: 'auto',
                  objectFit: 'cover',
                  borderRadius: 2,
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.02)',
                  },
                }}
                onError={(e) => {
                  // Fallback to default image if API image fails to load
                  e.currentTarget.src = '/images/account_member_card.png';
                }}
              />
            </Tooltip>
          </Box>

          {/* Membership Details - 50% width on desktop */}
          <Box sx={{ flex: { xs: '1', md: '0 0 50%' } }}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                height: '97%',
                borderRadius: 3,
                bgcolor: '#f8f9fa',
                border: '1px solid #e9ecef',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: '#212529',
                  mb: 2,
                  fontSize: { xs: '20px', md: '24px' },
                }}
              >
                {customer?.name || t('profile.membership.defaultName')}
              </Typography>

              {currentMembership?.membershipLevel && (
                <Chip
                  label={currentMembership.membershipLevel.name}
                  sx={{
                    bgcolor: currentMembership.membershipLevel.color || '#6c757d',
                    color: 'white',
                    fontWeight: 600,
                    mb: 2,
                  }}
                />
              )}

              {calculations?.currentSpent !== undefined && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography
                    sx={{
                      fontSize: '16px',
                      color: '#6c757d',
                      fontWeight: 700,
                    }}
                  >
                    {currentMembership?.membershipLevel?.name || t('profile.membership.current')} {t('profile.membership.accumulatedPurchases')}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      sx={{
                        fontSize: '16px',
                        color: '#6c757d',
                        fontWeight: 400,
                      }}
                    >
                      HK${calculations?.currentSpent?.toFixed(2) || '0.00'}
                    </Typography>
                    <Typography sx={{ color: '#6c757d' }}>&gt;</Typography>
                  </Box>
                </Box>
              )}

              {nextTier && calculations && (
                <>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(progressPercentage, 100)}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: '#e9ecef',
                      mb: 2,
                      '& .MuiLinearProgress-bar': {
                        bgcolor: '#b8957b',
                        borderRadius: 4,
                      },
                    }}
                  />

                  <Typography
                    sx={{
                      fontSize: '14px',
                      color: '#6c757d',
                      fontWeight: 500,
                    }}
                  >
                    {t('profile.membership.spendMoreToUpgrade')
                      .replace('{amount}', `HK$${nextTier.remainingToSpend.toFixed(2)}`)
                      .replace('{tier}', nextTier.name)}
                  </Typography>
                </>
              )}
            </Paper>
          </Box>
        </Box>

        {/* Order Status and Quick Actions Row */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 3, mr: '20px' }}>
          {/* Order Status */}
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              borderRadius: 3,
              bgcolor: '#f8f9fa',
              border: '1px solid #e9ecef',
              transition: 'all 0.2s ease-in-out',
              flex: { xs: 1, md: '0 0 50%' },
              '&:hover': {
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                transform: 'translateY(-2px)',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              {/* Order Status Indicators */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flex: 1 }}>
                {/* Awaiting Shipment */}
                <Box sx={{ textAlign: 'center', flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: '32px',
                      fontWeight: 700,
                      color: '#212529',
                      mb: 0.5,
                    }}
                  >
                    {getOrderCounts().awaitingShipment}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      color: '#6c757d',
                      lineHeight: 1.2,
                    }}
                  >
                    {t('profile.orderStatus.awaitingShipment')}
                  </Typography>
                </Box>

                {/* Shipped */}
                <Box sx={{ textAlign: 'center', flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: '32px',
                      fontWeight: 700,
                      color: '#212529',
                      mb: 0.5,
                    }}
                  >
                    {getOrderCounts().shipped}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      color: '#6c757d',
                      lineHeight: 1.2,
                    }}
                  >
                    {t('profile.orderStatus.shipped')}
                  </Typography>
                </Box>

                {/* Ready for Pickup */}
                <Box sx={{ textAlign: 'center', flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: '32px',
                      fontWeight: 700,
                      color: '#212529',
                      mb: 0.5,
                    }}
                  >
                    {getOrderCounts().readyForPickup}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      color: '#6c757d',
                      lineHeight: 1.2,
                    }}
                  >
                    {t('profile.orderStatus.readyForPickup')}
                  </Typography>
                </Box>
              </Box>

              {/* View Orders Button */}
              <Button
                onClick={() => navigate('/my-orders')}
                variant="contained"
                sx={{
                  bgcolor: '#B3907A',
                  color: 'white',
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                  px: 1,
                  py: 1,
                  minWidth: 'auto',
                  ml: 2,
                  '&:hover': {
                    bgcolor: '#9a7c68',
                  },
                }}
              >
                {t('profile.orderStatus.viewOrders')}
              </Button>
            </Box>
          </Paper>

          {/* Quick Actions */}
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              borderRadius: 3,
              bgcolor: '#f8f9fa',
              border: '1px solid #e9ecef',
              transition: 'all 0.2s ease-in-out',
              flex: { xs: 1, md: '0 0 50%' },
              '&:hover': {
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                transform: 'translateY(-2px)',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              {/* My Coupons */}
              <Box
                onClick={() => navigate('/my-coupons')}
                sx={{ textAlign: 'center', flex: 1, cursor: 'pointer' }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    margin: '0 auto',
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <img
                    src="/icons/ic_account_coupon.svg"
                    alt="Coupons"
                    width={32}
                    height={32}
                  />
                </Box>
                <Typography
                  sx={{
                    fontSize: '14px',
                    color: '#212529',
                    fontWeight: 500,
                  }}
                >
                  {t('profile.quickActions.myCoupons')}
                </Typography>
              </Box>

              {/* Messages */}
              <Box sx={{ textAlign: 'center', flex: 1, cursor: 'pointer' }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    margin: '0 auto',
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <img
                    src="/icons/ic_account_message.svg"
                    alt="Messages"
                    width={32}
                    height={32}
                  />
                </Box>
                <Typography
                  sx={{
                    fontSize: '14px',
                    color: '#212529',
                    fontWeight: 500,
                  }}
                >
                  {t('profile.quickActions.messages')}
                </Typography>
              </Box>

              {/* Wishlist */}
              <Box
                sx={{ textAlign: 'center', flex: 1, cursor: 'pointer' }}
                onClick={() => navigate('/wishlist')}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    margin: '0 auto',
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                  </svg>
                </Box>
                <Typography
                  sx={{
                    fontSize: '14px',
                    color: '#212529',
                    fontWeight: 500,
                  }}
                >
                  {t('profile.quickActions.wishlist')}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* My Points */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 3,
            bgcolor: '#f8f9fa',
            border: '1px solid #e9ecef',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
              transform: 'translateY(-2px)',
            },
          }}
        >
          <Box
            onClick={handlePointsDialogOpen}
            sx={{ cursor: 'pointer' }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: '#212529',
                mb: 2,
              }}
            >
              {t('profile.myPoints.title')}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                sx={{
                  fontSize: '24px',
                  fontWeight: 700,
                  color: '#dc3545',
                }}
              >
                {totalPoints || 0}
              </Typography>
              <Typography sx={{ color: '#6c757d' }}>&gt;</Typography>
            </Box>
          </Box>
        </Paper>

        {/* Point Offers */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 3,
            bgcolor: '#f8f9fa',
            border: '1px solid #e9ecef',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
              transform: 'translateY(-2px)',
            },
          }}
        >
          <Box
            onClick={() => navigate('/point-offers')}
            sx={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: '#212529',
              }}
            >
              {t('profile.pointOffers.title')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#6c757d',
                }}
              >
                {t('profile.pointOffers.cta')}
              </Typography>
              <Typography sx={{ color: '#6c757d' }}>&gt;</Typography>
            </Box>
          </Box>
        </Paper>

        {/* Account Settings - Personal Information and Logout */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            bgcolor: '#f8f9fa',
            border: '1px solid #e9ecef',
            overflow: 'hidden',
          }}
        >
          {/* Personal Information Section */}
          <Box
            onClick={handleProfileDialogOpen}
            sx={{
              px: 3,
              py: 1.5,
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                bgcolor: '#f0f0f0',
              },
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '16px',
                color: '#212529',
              }}
            >
              {t('profile.personalInfo.title')}
            </Typography>
          </Box>

          {/* Divider */}
          <Box
            sx={{
              height: '1px',
              bgcolor: '#e9ecef',
              mx: 3,
            }}
          />

          {/* Referral Code Section */}
          <Box
            sx={{
              px: 3,
              py: 1.5,
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '16px',
                color: '#212529',
                mb: 1,
              }}
            >
              {t('profile.referralCode.title')}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography
                sx={{
                  fontSize: '18px',
                  fontWeight: 700,
                  color: '#28a745',
                  fontFamily: 'monospace',
                  letterSpacing: '1px',
                }}
              >
                {referralCodeData?.code || (loading ? 'Loading...' : 'Not available')}
              </Typography>

              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  const codeToCopy = referralCodeData?.code || '';
                  if (codeToCopy && codeToCopy !== 'Loading...' && codeToCopy !== 'Not available') {
                    navigator.clipboard.writeText(codeToCopy);
                    // You can add a toast notification here if you have one
                  }
                }}
                disabled={!referralCodeData?.code || referralCodeData.code === 'Loading...' || referralCodeData.code === 'Not available'}
                sx={{
                  borderColor: '#28a745',
                  color: '#28a745',
                  '&:hover': {
                    borderColor: '#218838',
                    backgroundColor: '#f8fff9',
                  },
                  '&:disabled': {
                    borderColor: '#ccc',
                    color: '#ccc',
                  },
                  minWidth: 'auto',
                  px: 2,
                }}
              >
                {t('profile.referralCode.copy')}
              </Button>
            </Box>

            <Typography
              sx={{
                fontSize: '12px',
                color: '#6c757d',
                mt: 1,
              }}
            >
              {t('profile.referralCode.description')}
            </Typography>
          </Box>

          {/* Logout Section */}
          <Box
            onClick={handleLogout}
            sx={{
              px: 3,
              py: 1.5,
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                bgcolor: '#f0f0f0',
              },
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '16px',
                color: '#212529',
                mb: 2,
              }}
            >
              {t('profile.account.logout')}
            </Typography>

            <Typography
              sx={{
                fontSize: '14px',
                color: '#6c757d',
              }}
            >
              {t('profile.account.deleteAccountInfo')}
            </Typography>
          </Box>
        </Paper>
      </Container>

      {/* QR Code Dialog */}
      <Dialog
        open={qrDialogOpen}
        onClose={handleQrDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 0,
            overflow: 'hidden',
          }
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          {/* Header */}
          <Box sx={{
            p: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #e9ecef'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
              <Box
                component="img"
                src={config?.storeWalletLogo || "/images/logo.svg"}
                alt={config?.storeName || "Hatsuhiken Logo"}
                sx={{
                  height: '100%',
                  objectFit: 'contain',
                }}
              />
            </Box>
            <IconButton onClick={handleQrDialogClose} sx={{ color: '#6c757d' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* QR Code and Member ID */}
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Box
              sx={{
                width: 200,
                height: 200,
                bgcolor: '#f8f9fa',
                border: '2px solid #e9ecef',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
                overflow: 'hidden',
              }}
            >
              {qrCodeDataUrl ? (
                <Box
                  component="img"
                  src={qrCodeDataUrl}
                  alt="QR Code"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                  }}
                />
              ) : (
                <Typography sx={{ color: '#6c757d', fontSize: '14px' }}>
                  {t('profile.qrCode.loading')}
                </Typography>
              )}
            </Box>

            <Typography sx={{
              fontSize: '24px',
              fontWeight: 700,
              color: '#b8957b',
              mb: 1
            }}>
              {profileData?.customer?.name || '--'}
            </Typography>

            <Typography sx={{
              fontSize: '16px',
              color: '#6c757d',
              mb: 3
            }}>
              {t('profile.qrCode.memberId')}: {currentMembership?.membershipNumber || '--'}
            </Typography>

            {/* Only show wallet button on mobile devices (iOS and Android) */}
            {(() => {
              const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
              const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

              if (!isMobile) {
                return null; // Hide button on desktop browsers
              }

              return (
                <Button
                  onClick={handleAddCard}
                  disabled={isAddingToWallet}
                  variant="contained"
                  sx={{
                    p: 1.5,
                    minHeight: 'auto',
                    height: 'auto',
                    backgroundColor: 'transparent',
                    border: 'none',
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      border: 'none',
                      boxShadow: 'none',
                    }
                  }}
                  startIcon={isAddingToWallet ? <CircularProgress size={20} /> : null}
                >
                  {isAddingToWallet ? (
                    t('profile.wallet.addingToWallet')
                  ) : isCertificateError ? (
                    t('profile.wallet.retryAddingToWallet')
                  ) : isIOS ? (
                    // Use Apple Wallet button for iOS devices with internationalization
                    <img
                      src={i18n.language === 'zh' ? "/icons/cn_add_to_apple_wallet.svg" : "/icons/en_add_to_apple_wallet.svg"}
                      alt="Add to Apple Wallet"
                      style={{
                        height: '48px',
                        width: 'auto',
                        display: 'block'
                      }}
                    />
                  ) : (
                    t('profile.wallet.addToWalletPass')
                  )}
                </Button>
              );
            })()}

            {/* Only show wallet error on mobile devices */}
            {(() => {
              const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

              if (!isMobile || !walletError) {
                return null;
              }

              return (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'error.main',
                      mb: 1,
                      textAlign: 'center',
                      fontSize: '0.875rem'
                    }}
                  >
                    {walletError}
                  </Typography>
                </Box>
              );
            })()}
          </Box>


          {/* Close Button */}
          <Box sx={{ p: 3, borderTop: '1px solid #e9ecef' }}>
            <Button
              fullWidth
              onClick={handleQrDialogClose}
              sx={{
                bgcolor: '#fd7e14',
                color: 'white',
                py: 1.5,
                borderRadius: 2,
                fontWeight: 600,
                '&:hover': {
                  bgcolor: '#e8690b',
                },
              }}
            >
              {t('profile.qrCode.close')}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Points Details Dialog */}
      <Dialog
        open={pointsDialogOpen}
        onClose={handlePointsDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 0,
            overflow: 'hidden',
          }
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          {/* Header */}
          <Box sx={{
            p: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #e9ecef'
          }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: '#212529',
              }}
            >
              {t('profile.myPoints.title')}
            </Typography>
            <IconButton onClick={handlePointsDialogClose} sx={{ color: '#6c757d' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Points Details */}
          <Box sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Total Points */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ color: '#6c757d', fontWeight: 500 }}>
                  {t('profile.points.totalPoints')}:
                </Typography>
                <Typography
                  sx={{
                    fontSize: '20px',
                    fontWeight: 600,
                    color: '#212529',
                  }}
                >
                  {totalPoints || 0}
                </Typography>
              </Box>

              {/* Available Points */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ color: '#6c757d', fontWeight: 500 }}>
                  {t('profile.points.availablePoints')}:
                </Typography>
                <Typography
                  sx={{
                    fontSize: '20px',
                    fontWeight: 600,
                    color: '#212529',
                  }}
                >
                  {rewardPointsData?.availablePoints || 0}
                </Typography>
              </Box>

              {/* Expired Points */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ color: '#6c757d', fontWeight: 500 }}>
                  {t('profile.points.expiredPoints')}:
                </Typography>
                <Typography
                  sx={{
                    fontSize: '20px',
                    fontWeight: 600,
                    color: '#212529',
                  }}
                >
                  {rewardPointsData?.expiredPoints || 0}
                </Typography>
              </Box>

              {/* Pending Expiry */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ color: '#6c757d', fontWeight: 500 }}>
                  {t('profile.points.pendingExpiry')}:
                </Typography>
                <Typography
                  sx={{
                    fontSize: '20px',
                    fontWeight: 600,
                    color: '#212529',
                  }}
                >
                  {rewardPointsData?.pendingExpiry || 0}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Close Button */}
          <Box sx={{ p: 3, borderTop: '1px solid #e9ecef' }}>
            <Button
              fullWidth
              onClick={handlePointsDialogClose}
              sx={{
                bgcolor: '#fd7e14',
                color: 'white',
                py: 1.5,
                borderRadius: 2,
                fontWeight: 600,
                '&:hover': {
                  bgcolor: '#e8690b',
                },
              }}
            >
              {t('profile.qrCode.close')}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Profile Details Dialog */}
      <Dialog
        open={profileDialogOpen}
        onClose={handleProfileDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 0,
            overflow: 'hidden',
          }
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          {/* Header */}
          <Box sx={{
            p: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #e9ecef'
          }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: '#212529',
              }}
            >
              {t('profile.personalInfo.title')}
            </Typography>
            <IconButton onClick={handleProfileDialogClose} sx={{ color: '#6c757d' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Profile Form */}
          <form onSubmit={handleSubmit(onSubmitProfile)}>
            <Box sx={{ p: 4 }}>
              <Stack spacing={3}>
                {/* Mobile Number */}
                <Box>
                  <Typography fontWeight={700} fontSize="16px">
                    * {t('auth.phoneNumber')}
                  </Typography>
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field }) => (
                      <MuiTelInput
                        {...field}
                        value={field.value || ''}
                        fullWidth
                        size="small"
                        variant="outlined"
                        disabled={updatingProfile}
                        sx={{
                          mt: 1,
                          '& .MuiTelInput-flagButton': {
                            borderColor: '#E1DACA',
                          },
                          '& .MuiTelInput-flagButton:hover': {
                            borderColor: '#E1DACA',
                          },
                          '& .MuiTelInput-flagButton.Mui-focused': {
                            borderColor: '#E1DACA',
                          },
                        }}
                        defaultCountry="HK"
                        forceCallingCode={true}
                        disableFormatting={false}
                        placeholder="Phone number"
                      />
                    )}
                  />
                </Box>

                {/* First Name */}
                <Box>
                  <Typography fontWeight={700} fontSize="16px">
                    {t('auth.firstName')}
                  </Typography>
                  <Controller
                    name="firstName"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        placeholder={t('auth.firstName')}
                        fullWidth
                        size="small"
                        disabled={updatingProfile}
                        sx={{ mt: 1 }}
                      />
                    )}
                  />
                </Box>

                {/* Last Name */}
                <Box>
                  <Typography fontWeight={700} fontSize="16px">
                    {t('auth.lastName')}
                  </Typography>
                  <Controller
                    name="lastName"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        placeholder={t('auth.lastName')}
                        fullWidth
                        size="small"
                        disabled={updatingProfile}
                        sx={{ mt: 1 }}
                      />
                    )}
                  />
                </Box>

                {/* Date of Birth */}
                <Box>
                  <Typography fontWeight={700} fontSize="16px">
                    * {t('register.birthday')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <Controller
                        name="birthMonth"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            value={field.value || ''}
                            displayEmpty
                            disabled={updatingProfile}
                          >
                            <MenuItem value="" disabled>
                              {t('register.selectOption')}
                            </MenuItem>
                            {Array.from({ length: 12 }, (_, i) => (
                              <MenuItem key={i + 1} value={(i + 1).toString()}>
                                {(i + 1).toString().padStart(2, '0')}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                    </FormControl>
                    <TextField
                      {...register('birthDay')}
                      placeholder="Day"
                      size="small"
                      sx={{ flex: 1 }}
                      type="text"
                      disabled={updatingProfile}
                      inputProps={{ maxLength: 2 }}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d{0,2}$/.test(value)) {
                          setValue('birthDay', value);
                        }
                      }}
                    />
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <Controller
                        name="birthYear"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            value={field.value || ''}
                            displayEmpty
                            disabled={updatingProfile}
                          >
                            <MenuItem value="" disabled>
                              {t('register.selectOption')}
                            </MenuItem>
                            {Array.from({ length: 100 }, (_, i) => {
                              const year = new Date().getFullYear() - i;
                              return (
                                <MenuItem key={year} value={year.toString()}>
                                  {year}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        )}
                      />
                    </FormControl>
                  </Box>
                </Box>

                {/* Gender */}
                <Box>
                  <Typography fontWeight={700} fontSize="16px">
                    {t('register.gender')}
                  </Typography>
                  <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                    <Controller
                      name="gender"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          value={field.value || ''}
                          displayEmpty
                          disabled={updatingProfile}
                        >
                          <MenuItem value="" disabled>
                            {t('register.selectOption')}
                          </MenuItem>
                          <MenuItem value="male">{t('register.male')}</MenuItem>
                          <MenuItem value="female">{t('register.female')}</MenuItem>
                          <MenuItem value="other">{t('register.other')}</MenuItem>
                        </Select>
                      )}
                    />
                  </FormControl>
                </Box>

                {/* Email */}
                <Box>
                  <Typography fontWeight={700} fontSize="16px">
                    {t('auth.email')}
                  </Typography>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        placeholder={t('auth.emailHelper')}
                        fullWidth
                        size="small"
                        sx={{ mt: 1 }}
                        disabled={true}
                        helperText="Email cannot be updated"
                      />
                    )}
                  />
                </Box>

                {/* Note */}
                <Typography variant="caption" sx={{ color: '#6c757d', mt: 1 }}>
                  * Traditional Chinese input allowed for Taiwan, Hong Kong, and Macao. For other regions, please input in English.
                </Typography>
              </Stack>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ p: 3, borderTop: '1px solid #e9ecef' }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleProfileDialogClose}
                  disabled={updatingProfile}
                  sx={{
                    borderColor: '#e9ecef',
                    color: '#6c757d',
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: '#b8957b',
                      color: '#b8957b',
                    },
                    '&:disabled': {
                      borderColor: '#ccc',
                      color: '#ccc',
                    },
                  }}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  fullWidth
                  type="submit"
                  disabled={updatingProfile}
                  sx={{
                    bgcolor: '#fd7e14',
                    color: 'white',
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: '#e8690b',
                    },
                    '&:disabled': {
                      bgcolor: '#ccc',
                      color: '#666',
                    },
                  }}
                >
                  {updatingProfile ? 'Updating...' : t('common.save')}
                </Button>
              </Box>
            </Box>
          </form>
        </DialogContent>
      </Dialog>

      {/* Toast Notification */}
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToast({ ...toast, open: false })}
          severity={toast.severity}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
} 
